import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ShieldAlert, ShieldCheck, ChevronDown, ChevronUp } from 'lucide-react';
import ProofStatusBadge from './ProofStatusBadge';

const CONSTRAINTS = [
  {
    id: 'CT-001',
    label: 'CloudTrail org logging must never be disabled',
    scope: 'All 47 accounts · AWS Organizations',
    framework: 'SOC 2 CC7.2 · PCI DSS 10.1',
    status: 'UPHELD',
    lastVerified: 'Apr 21, 2026 · 02:01 UTC',
    evidenceId: 'EVD-CT001-20260421',
    proofStatus: 'PROVEN',
    depth: 9.8,
  },
  {
    id: 'CT-002',
    label: 'PCI-scoped workloads must never operate without encryption evidence',
    scope: 'stratum-payments-prod · stratum-banking-prod',
    framework: 'PCI DSS 3.4 · DORA Art. 9',
    status: 'VIOLATED',
    lastVerified: 'Apr 12, 2026 — Apr 21, 2026',
    evidenceId: 'EVD-CT002-INV2026042',
    proofStatus: 'PROVEN',
    depth: 9.1,
    violationDetail: 'Config: Encryption at Rest inactive for 9 days. No authorized disable event found in CloudTrail.',
    investigationId: 'INV-2026-042',
  },
  {
    id: 'CT-003',
    label: 'Dev identities must never assume production IAM roles',
    scope: 'stratum-dev-sandbox → all production accounts',
    framework: 'ISO 27001 A.9.4 · SOC 2 CC6.3',
    status: 'VIOLATED',
    lastVerified: 'Apr 14, 2026 · 02:17 UTC',
    evidenceId: 'EVD-CT003-INV2026042',
    proofStatus: 'PROVEN',
    depth: 9.4,
    violationDetail: 'developer-console-access assumed lambda-payments-processor role in stratum-payments-prod. 2 successful traversals recorded.',
    investigationId: 'INV-2026-042',
  },
  {
    id: 'CT-004',
    label: 'Crown-jewel accounts must never lose telemetry coverage',
    scope: 'stratum-payments-prod · stratum-banking-prod',
    framework: 'DORA Art. 25 · PCI DSS 10.5',
    status: 'VIOLATED',
    lastVerified: 'Apr 12, 2026',
    evidenceId: 'EVD-CT004-20260412',
    proofStatus: 'PROVEN',
    depth: 8.9,
    violationDetail: 'Config rule evaluations absent for 9 days in stratum-payments-prod. No DisableConfigRule event found.',
    investigationId: 'INV-2026-042',
  },
  {
    id: 'CT-005',
    label: 'Pipeline-bypass production deployments are structurally prohibited',
    scope: 'All production-classified ECS / EKS services',
    framework: 'SOC 2 CC8.1 · ISO 27001 A.12.1.2',
    status: 'UPHELD',
    lastVerified: 'Apr 21, 2026 · 02:01 UTC',
    evidenceId: 'EVD-CT005-20260421',
    proofStatus: 'PROVEN',
    depth: 8.6,
  },
  {
    id: 'CT-006',
    label: 'Encryption keys must never be decrypted by identities outside approved list',
    scope: 'payments-cmk · banking-cmk · reporting-cmk',
    framework: 'PCI DSS 3.6 · DORA Art. 9',
    status: 'UPHELD',
    lastVerified: 'Apr 21, 2026 · 02:01 UTC',
    evidenceId: 'EVD-CT006-20260421',
    proofStatus: 'INFERRED',
    depth: 6.2,
    reasonCode: 'MISSING_CLOUDTRAIL_DATA_EVENT',
  },
];

export default function GovernanceConstraintPanel({ navigate }) {
  const [expanded, setExpanded] = useState(null);
  const violated = CONSTRAINTS.filter((c) => c.status === 'VIOLATED');
  const upheld = CONSTRAINTS.filter((c) => c.status === 'UPHELD');

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="governance-constraints">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="flex items-center gap-2">
            {violated.length > 0 && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            )}
            <h3 className="text-sm font-bold text-apex-ink">Governance Invariants</h3>
          </div>
          <p className="text-[11px] text-apex-neutral mt-0.5 font-mono">
            {violated.length} violated · {upheld.length} upheld · These are structural laws, not findings
          </p>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono">
          <span className="flex items-center gap-1 text-red-600">
            <ShieldAlert className="w-3 h-3" /> {violated.length} VIOLATED
          </span>
          <span className="flex items-center gap-1 text-teal-600">
            <ShieldCheck className="w-3 h-3" /> {upheld.length} UPHELD
          </span>
        </div>
      </div>

      <div className="space-y-2">
        {CONSTRAINTS.map((c) => (
          <div
            key={c.id}
            className={`rounded-lg border transition-all ${
              c.status === 'VIOLATED' ? 'border-red-200 bg-red-50' : 'border-slate-100 bg-slate-50/60'
            }`}
          >
            <div
              role="button"
              tabIndex={0}
              className="w-full flex items-start gap-3 p-3 text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-apex-lens/40"
              onClick={(e) => {
                // Don't toggle when the click originated from the inert proof badge.
                if (e.target.closest?.(`[data-testid="constraint-${c.id}-proof"]`)) return;
                setExpanded(expanded === c.id ? null : c.id);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setExpanded(expanded === c.id ? null : c.id);
                }
              }}
              aria-expanded={expanded === c.id}
              aria-controls={`constraint-${c.id}-detail`}
              data-testid={`constraint-${c.id}`}
            >
              <div className={`mt-0.5 shrink-0 ${c.status === 'VIOLATED' ? 'text-red-500' : 'text-teal-600'}`}>
                {c.status === 'VIOLATED' ? <ShieldAlert className="w-4 h-4" /> : <ShieldCheck className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[11px] font-bold font-mono ${c.status === 'VIOLATED' ? 'text-red-700' : 'text-slate-600'}`}>
                    {c.id}
                  </span>
                  <span className={`text-[11px] font-semibold ${c.status === 'VIOLATED' ? 'text-red-800' : 'text-apex-ink'}`}>
                    {c.label}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <ProofStatusBadge status={c.proofStatus} reasonCode={c.reasonCode} size="xs" testId={`constraint-${c.id}-proof`} displayOnly />
                  <span className="text-[10px] text-apex-neutral font-mono">{c.scope}</span>
                </div>
              </div>
              {expanded === c.id
                ? <ChevronUp className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                : <ChevronDown className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
              }
            </div>

            <AnimatePresence>
              {expanded === c.id && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="overflow-hidden"
                >
                  <div className="px-3 pb-3 pt-0 space-y-2 border-t border-slate-200/60">
                    <div className="grid grid-cols-2 gap-2 mt-2 text-[10px] font-mono">
                      <div>
                        <div className="text-apex-neutral mb-0.5">Framework</div>
                        <div className="text-apex-ink">{c.framework}</div>
                      </div>
                      <div>
                        <div className="text-apex-neutral mb-0.5">Last verified</div>
                        <div className="text-apex-ink">{c.lastVerified}</div>
                      </div>
                      <div>
                        <div className="text-apex-neutral mb-0.5">Evidence ID</div>
                        <div className="text-apex-lens truncate">{c.evidenceId}</div>
                      </div>
                      <div>
                        <div className="text-apex-neutral mb-0.5">Proof depth</div>
                        <div className="text-apex-ink">{c.depth}/10</div>
                      </div>
                    </div>
                    {c.violationDetail && (
                      <div className="rounded-md bg-red-100 border border-red-200 p-2 text-[11px] text-red-800">
                        <span className="font-bold">Violation: </span>{c.violationDetail}
                      </div>
                    )}
                    {c.investigationId && (
                      <button
                        type="button"
                        onClick={() => navigate?.(`/lens/investigations?id=${c.investigationId}`)}
                        className="text-[11px] font-semibold text-apex-lens hover:text-teal-600 transition-colors inline-flex items-center gap-1"
                        data-testid={`constraint-${c.id}-inv-link`}
                      >
                        → Open {c.investigationId}
                      </button>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        ))}
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 text-[10px] font-mono text-slate-400">
        Invariants are structural governance laws. A violation is categorically different from a finding — it means a governance boundary was crossed. Violations auto-link to investigations.
      </div>
    </div>
  );
}
